/**
 * Small helper to log timing in New Relic for success/failure without duplicating code.
 * Usage:
 * const { run } = useTimedPageAction({ action: TimedPageActions.SEARCH_FRONTEND });
 * await run(async () => { ... });
 */
import { readAuthContextAttributes } from '@/plugins/auth-context.ts';
import NewRelicBrowser from '@/plugins/newrelic-browser.ts';

type UseTimedPageActionOptions = {
    action?: string;
    additionalData?: Record<string, unknown>;
};

type TimedPageAction = {
    run: <T>(callback: () => Promise<T> | T) => Promise<T>;
    markSuccess: () => void;
    markFailure: (error: unknown) => never;
};

export function useTimedPageAction({
    action,
    additionalData,
}: UseTimedPageActionOptions): TimedPageAction {
    const startedAt = performance.now();
    let finished = false;

    const buildAttributes = (): Record<string, unknown> => ({
        ...readAuthContextAttributes(),
        ...(additionalData ? { ...additionalData } : {}),
        app_version: process.env.VUE_APP_VERSION || 'unknown',
        app_build_id: process.env.VUE_APP_BUILD_ID || 'unknown',
    });

    const logDuration = (suffix: string): void => {
        if (!action) {
            return;
        }

        const duration = Math.round(performance.now() - startedAt);

        NewRelicBrowser.addPageAction(`${action}${suffix}`, {
            time: duration,
            ...buildAttributes(),
        });
    };

    const markSuccess = (): void => {
        if (finished) return;
        finished = true;
        logDuration('_success');
    };

    const markFailure = (error: unknown): never => {
        if (!finished) {
            finished = true;
            logDuration('_failure');
        }
        throw error;
    };

    const run = async <T>(callback: () => Promise<T> | T): Promise<T> => {
        try {
            const result = await callback();
            markSuccess();
            return result;
        } catch (error) {
            return markFailure(error);
        }
    };

    return {
        run,
        markSuccess,
        markFailure,
    };
}
